import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'

import { CirculatingPumpType, DecentralizedElectricHeatingType, DecentralizedGasHeatingType, DecentralizedHeatPumpHeatingType, ElectricHeatingFuelType, GasFuelType, GeneratedByType, HeatPumpFuelType, HeatTransferType, HeaterLocationType, HeatingCombinationType, HeatingFuelType, HeatingPipeType, IndividualStoveFuelType, NightServiceType, OilFuelType, WaterLocationType, WoodPelletsEnergySourceType, WoodPelletsFuelType, DistrictHeatingFuelType, handoverAndPumpingFields } from 'constants/purchaseFlow'
import { GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Label } from 'components/common/Label'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUINumberField } from 'components/common/MUINumberField'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUIRadio } from 'components/common/MUIRadio'
import { MUITooltip } from 'components/common/MUITooltip'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import type { FuelTypeAllowedEnums } from 'constants/purchaseFlow'
import type { HeatingSystemItemDTO } from 'models/purchaseFlow/energyCertificateOfNeedDTO'

import type { UseEnergyCertificateForm } from '../../useEnergyCertificateForm'
import { PRIMARY_FIELD_WIDTH } from './layout'

/** The option list and its copy key for a heater's fuel. */
const fuelOptionEnum = (fuel: HeatingFuelType | null | undefined): { key: string; enum: Record<string, string> | null } => {
  switch (fuel) {
    case HeatingFuelType.OIL:
      return { key: 'oil_fuel_type', enum: OilFuelType }
    case HeatingFuelType.GAS:
      return { key: 'gas_fuel_type', enum: GasFuelType }
    case HeatingFuelType.WOOD_PELLETS_SOLIDS:
      return { key: 'wood_pelets_fuel_type', enum: WoodPelletsFuelType }
    case HeatingFuelType.DISTRICT_HEATING:
      return { key: 'district_heating_fuel_type', enum: DistrictHeatingFuelType }
    case HeatingFuelType.HEAT_PUMP:
      return { key: 'heat_pump_fuel_type', enum: HeatPumpFuelType }
    case HeatingFuelType.ELECTRIC_HEATING:
      return { key: 'electric_heating_fuel_type', enum: ElectricHeatingFuelType }
    case HeatingFuelType.INDIVIDUAL_STOVE:
      return { key: 'individual_stove_fuel_type', enum: IndividualStoveFuelType }
    default:
      return { key: '', enum: null }
  }
}

/** The decentralized-heating type list and its copy key for what generates the heat. */
const decentralizedTypeMeta = (generatedBy: GeneratedByType): { key: string; enum: Record<string, string> | null } => {
  switch (generatedBy) {
    case GeneratedByType.GAS:
      return { key: 'decentralized_gas_heating_type', enum: DecentralizedGasHeatingType }
    case GeneratedByType.HEAT_PUMP:
      return { key: 'decentralized_heat_pump_heating_type', enum: DecentralizedHeatPumpHeatingType }
    case GeneratedByType.ELECTRIC:
    default:
      return { key: 'decentralized_electric_heating_type', enum: DecentralizedElectricHeatingType }
  }
}

/** One heating system: fuel and its option, generation, distribution, storage and hot water. */
interface HeaterRowProps {
  id: string
  index: number
  heater: HeatingSystemItemDTO
  form: UseEnergyCertificateForm
}

export const HeaterRow: React.FC<HeaterRowProps> = ({ id, index, heater, form }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'energy_certificate_step.energy_heating_page.heating_system' })
  const { setHeater, setHeaterShare } = form

  const set = (partial: Partial<HeatingSystemItemDTO>) => setHeater(id, partial)

  const showHandover = !!heater.heatingFuel && handoverAndPumpingFields.has(heater.heatingFuel)
  const isCentralHotWater = heater.heatingCombinedWith.has(HeatingCombinationType.CENTRAL_HOT_WATER_GENERATION)
  // The years block the step (the backend rejects a null one), so once the client has started this heater an
  // empty year is shown in red rather than leaving them to guess why the tab stays incomplete (ISSUE-4191).
  const flagMissingYear = !!heater.heatingFuel

  const fuelOption = fuelOptionEnum(heater.heatingFuel)
  const decentralizedMeta = decentralizedTypeMeta(heater.generatedBy)

  const combinedValues: ToggleFilterValue<HeatingCombinationType>[] = Object.values(HeatingCombinationType).map((type) => ({
    value: type,
    displayName: t(`heating_combined_with_type.${type}`),
  }))

  const toggleCombined = (type: HeatingCombinationType, selected: boolean) => {
    const next = new Set(heater.heatingCombinedWith)
    if (selected) next.add(type)
    else next.delete(type)
    set({ heatingCombinedWith: next })
  }

  return (
    <Stack alignItems="flex-start" gap={1.6} width="100%">

      {/* GENERATOR YEAR / DESIGNATION / POWER */}
      <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }}>
        <MUINumberField min={0} required inputBoxWidth="100%" label={t('generator_year_manufacture')} isError={flagMissingYear && !heater.generatorYearOfManufacture} value={heater.generatorYearOfManufacture} onChange={(v) => set({ generatorYearOfManufacture: v })} />
      </Box>

      <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }}>
        <MUIInputField label={t('heater_designation')} value={heater.heatingDesignation} onChange={(e) => set({ heatingDesignation: e.target.value })} />
      </Box>

      <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }}>
        <MUINumberField min={0} inputBoxWidth="100%" label={t('heating_power')} suffix="kW" value={heater.heatingPower} onChange={(v) => set({ heatingPower: v })} />
      </Box>

      {/* HEATER LOCATION */}
      <Stack gap=".8rem">
        <Label text={t('heater_location')} />
        {Object.values(HeaterLocationType).map((type) => (
          <Stack key={type} gap=".8rem" flexDirection="row">
            <MUIRadio checked={heater.heaterLocation === type} onChange={() => set({ heaterLocation: type })} />
            <Label color={GRAY_900} text={t(`heater_location_type.${type}`)} />
          </Stack>
        ))}
      </Stack>

      {/* NIGHT SERVICE */}
      <Stack gap=".8rem">
        <Label text={t('night_service')} />
        {Object.values(NightServiceType).map((type) => (
          <Stack key={type} gap=".8rem" flexDirection="row">
            <MUIRadio checked={heater.nightService === type} onChange={() => set({ nightService: type })} />
            <Label color={GRAY_900} text={t(`night_service_type.${type}`)} />
          </Stack>
        ))}
      </Stack>

      {/* HEATING FUEL */}
      <Stack gap="1.8rem" width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(_isOpen, action) => (
            <MUIDropdownInput readOnly required label={t('heating_fuel')} type={DropdownInputTypes.SELECTOR} value={t(heater.heatingFuel ? `heating_fuel_type.${heater.heatingFuel}` : 'select_heating_fuel_option')} onClick={action} />
          )}
        >
          {Object.values(HeatingFuelType).map((type) => (
            <MUIDropdownItem key={type} onClick={() => set({ heatingFuel: type })}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`heating_fuel_type.${type}`)}</Typography>
            </MUIDropdownItem>
          ))}
        </MUIDropdown>
      </Stack>

      {/* FUEL OPTIONS */}
      {!!heater.heatingFuel && fuelOption.enum && <>

        <Stack gap="1.8rem" width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
          <MUIDropdown
            sx={{ width: '100%' }}
            button={(_isOpen, action) => (
              <MUIDropdownInput
                readOnly
                required
                label={t(`heating_fuel_type.${heater.heatingFuel}`)}
                type={DropdownInputTypes.SELECTOR}
                value={t(heater.heatingFuelOption && Object.values(fuelOption.enum!).includes(heater.heatingFuelOption) ? `${fuelOption.key}.${heater.heatingFuelOption}` : 'select_heating_fuel_type_option')}
                onClick={action}
              />
            )}
          >
            {Object.values(fuelOption.enum).map((type) => (
              <MUIDropdownItem key={type} onClick={() => set({ heatingFuelOption: type as FuelTypeAllowedEnums })}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`${fuelOption.key}.${type}`)}</Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>
        </Stack>

        {heater.heatingFuel === HeatingFuelType.WOOD_PELLETS_SOLIDS &&
          <Stack gap="1.8rem" width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_isOpen, action) => (
                <MUIDropdownInput readOnly required label={t('wood_pallets_energy_source_title')} type={DropdownInputTypes.SELECTOR} value={t(heater.woodPalletsEnergySource ? `wood_pallets_energy_source.${heater.woodPalletsEnergySource}` : 'select_wood_pallets_energy_source_option')} onClick={action} />
              )}
            >
              {Object.values(WoodPelletsEnergySourceType).map((type) => (
                <MUIDropdownItem key={type} onClick={() => set({ woodPalletsEnergySource: type })}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`wood_pallets_energy_source.${type}`)}</Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>
        }

      </>}

      {/* HYDRAULIC BALANCING */}
      <Stack flexDirection="row" alignItems="center" gap=".8rem">
        <MUICheckbox checked={heater.isHydraulicBalancing} onChange={() => set({ isHydraulicBalancing: !heater.isHydraulicBalancing })} />
        <Label color={GRAY_900} text={t('hydraulic_balancing')} />
      </Stack>

      {/* HANDOVER AND PUMPING */}
      {showHandover && <>

        <Box paddingTop={2} width="100%">
          <Typography color={GRAY_900} variant="text-md" fontWeight={600}>{t('handover_and_pumping')}</Typography>
          <MUIDivider margin={2} />
        </Box>

        <Stack gap="1.8rem" width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
          <MUIDropdown
            sx={{ width: '100%' }}
            button={(_isOpen, action) => (
              <MUIDropdownInput readOnly required label={t('heat_transfer')} type={DropdownInputTypes.SELECTOR} value={t(heater.heatTransferType ? `heat_transfer_type.${heater.heatTransferType}` : 'select_heat_transfer_option')} onClick={action} />
            )}
          >
            {Object.values(HeatTransferType).map((type) => (
              <MUIDropdownItem key={type} onClick={() => set({ heatTransferType: type })}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`heat_transfer_type.${type}`)}</Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>
        </Stack>

        <Stack gap="1.8rem" width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
          <MUIDropdown
            sx={{ width: '100%' }}
            button={(_isOpen, action) => (
              <MUIDropdownInput readOnly required label={t('heat_line')} type={DropdownInputTypes.SELECTOR} value={t(heater.heatingPipeType ? `heat_line_type.${heater.heatingPipeType}` : 'select_heat_line_option')} onClick={action} />
            )}
          >
            {Object.values(HeatingPipeType).map((type) => (
              <MUIDropdownItem key={type} onClick={() => set({ heatingPipeType: type })}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`heat_line_type.${type}`)}</Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>
        </Stack>

        <Stack gap="1.8rem" width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
          <MUIDropdown
            sx={{ width: '100%' }}
            button={(_isOpen, action) => (
              <MUIDropdownInput
                readOnly
                required
                label={t('circulation_pumps')}
                tooltip={<MUITooltip content={t('circulation_pumps_tooltip')}><InfoOutlinedIcon fontSize="medium" sx={{ paddingTop: '.4rem' }} /></MUITooltip>}
                type={DropdownInputTypes.SELECTOR}
                value={t(heater.circulatingPumpType ? `circulating_pump_type.${heater.circulatingPumpType}` : 'select_circulation_pumps_option')}
                onClick={action}
              />
            )}
          >
            {Object.values(CirculatingPumpType).map((type) => (
              <MUIDropdownItem key={type} onClick={() => set({ circulatingPumpType: type })}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`circulating_pump_type.${type}`)}</Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>
        </Stack>

      </>}

      {/* HEATING COMBINED WITH */}
      <Stack gap={1.6} width="100%">
        <Label text={t('heating_combined_with_title')} />
        <Stack gap={1.6} width={{ xs: '100%', lg: 'max-content' }}>
          <SelectableButtonsGrid
            values={combinedValues}
            selectedValue={Array.from(heater.heatingCombinedWith)}
            onSelect={(type) => toggleCombined(type, true)}
            onUnselect={(type) => toggleCombined(type, false)}
            showCheckbox
            activeButtonClickable
          />
        </Stack>
      </Stack>

      {/* HOT WATER (central / decentralized branch) */}
      <Box paddingTop={2} width="100%">
        <Typography color={GRAY_900} variant="text-md" fontWeight={600}>{t(isCentralHotWater ? 'centralized_hot_water' : 'decentralized_hot_water')}</Typography>
        <MUIDivider margin={2} />
      </Box>

      {isCentralHotWater
        ? <BorderBoxWrapper padding={2} width="100%">

            <Stack gap=".8rem">
              <Label text={t('central_water_location')} />
              {Object.values(WaterLocationType).map((type) => (
                <Stack key={type} gap=".8rem" flexDirection="row">
                  <MUIRadio checked={heater.waterLocation === type} onChange={() => set({ waterLocation: type })} />
                  <Label color={GRAY_900} text={t(`centralized_water_location_type.${type}`)} />
                </Stack>
              ))}
            </Stack>

            <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }} paddingTop={1.6}>
              <MUINumberField min={0} required inputBoxWidth="100%" label={t('year_of_construction')} isError={flagMissingYear && !heater.yearOfConstruction} value={heater.yearOfConstruction} onChange={(v) => set({ yearOfConstruction: v })} />
            </Box>

            <Stack flexDirection="row" alignItems="center" gap=".8rem" marginTop={1.6}>
              <MUICheckbox checked={heater.isHotWaterCirculation} onChange={() => set({ isHotWaterCirculation: !heater.isHotWaterCirculation })} />
              <Label color={GRAY_900} text={t('isHotWaterCirculation')} />
            </Stack>

          </BorderBoxWrapper>
        : <BorderBoxWrapper padding={2} width="100%">

            <Stack gap=".8rem">
              <Label text={t('generated_by')} />
              {Object.values(GeneratedByType).map((type) => (
                <Stack key={type} gap=".8rem" flexDirection="row">
                  <MUIRadio checked={heater.generatedBy === type} onChange={() => set({ generatedBy: type })} />
                  <Label color={GRAY_900} text={t(`generated_by_type.${type}`)} />
                </Stack>
              ))}
            </Stack>

            <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }} paddingTop={1.6}>
              <MUINumberField min={0} required inputBoxWidth="100%" label={t('year_of_construction')} isError={flagMissingYear && !heater.yearOfConstruction} value={heater.yearOfConstruction} onChange={(v) => set({ yearOfConstruction: v })} />
            </Box>

            <Stack gap="1.8rem" paddingTop={1.6} width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
              <MUIDropdown
                sx={{ width: '100%' }}
                button={(_isOpen, action) => (
                  <MUIDropdownInput required readOnly label={t('decentralized_production')} type={DropdownInputTypes.SELECTOR} value={t(heater.decentralizedHeatingType ? `decentralized_production_type.${decentralizedMeta.key}.${heater.decentralizedHeatingType}` : 'select_decentralized_production_option')} onClick={action} />
                )}
              >
                {Object.values(decentralizedMeta.enum ?? {}).map((type) => (
                  <MUIDropdownItem key={type} onClick={() => set({ decentralizedHeatingType: type as HeatingSystemItemDTO['decentralizedHeatingType'] })}>
                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`decentralized_production_type.${decentralizedMeta.key}.${type}`)}</Typography>
                  </MUIDropdownItem>
                ))}
              </MUIDropdown>
            </Stack>

          </BorderBoxWrapper>
      }

      {/* TOTAL HEATING OUTPUT */}
      <Box paddingTop={2} width="100%">
        <Typography color={GRAY_900} variant="text-md" fontWeight={600}>{t('total_heating_output')}</Typography>
        <MUIDivider margin={2} />
      </Box>

      <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }}>
        <MUINumberField
          readOnly={index === 0}
          min={0}
          max={100}
          suffix="%"
          required
          inputBoxWidth="100%"
          label={t('share_of_total_heating_output')}
          value={heater.totalHeatingOutput}
          onChange={(v) => setHeaterShare(id, v)}
        />
      </Box>

    </Stack>
  )
}
